import React from "react";

const statusColors = {
  10: { bg: "#fff3cd", color: "#856404", label: "Pending" },   // orange/warning
  20: { bg: "#d4edda", color: "#155724", label: "Paid" },      // green/success
  30: { bg: "#f8d7da", color: "#721c24", label: "Overdue" },   // red/danger
};

const getStatusBadge = (status) => {
  const s = statusColors[status] || statusColors[10];
  return (
    <span
      style={{
        backgroundColor: s.bg,
        color: s.color,
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {s.label}
    </span>
  );
};

const formatDate = (dateVal) => {
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "-";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy}`;
};

const InstalmentSchedule = ({
  instalments = [],
  purchased = false,
  currencyCode = "",
  onPayNext,
  loading = false,
  selectable = false,
  selectedInstalments = [],
  onSelectionChange,
}) => {
  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 20 }}>
        <i className="fa fa-spinner fa-spin" style={{ fontSize: 20 }}></i>
        <p style={{ fontSize: 12, marginTop: 6 }}>Loading installment plan...</p>
      </div>
    );
  }

  if (!instalments || instalments.length === 0) {
    return null;
  }

  // For purchased mode: find the first pending instalment index
  const firstPendingIdx = purchased
    ? instalments.findIndex((inst) => inst.status === 10 || inst.status === 30)
    : -1;

  // Determine if a checkbox is enabled
  const isCheckboxEnabled = (idx) => {
    if (!selectable) return false;

    if (purchased) {
      // Only pending instalments (status 10 or 30) can be selected
      const inst = instalments[idx];
      if (inst.status === 20) return false; // already paid
      if (firstPendingIdx === -1) return false;
      // Contiguous from first pending: can only select if all prior pending are selected
      for (let i = firstPendingIdx; i < idx; i++) {
        if (instalments[i].status !== 20 && !selectedInstalments.includes(i + 1)) {
          return false;
        }
      }
      return true;
    } else {
      // Pre-purchase: instalment 1 always selected, contiguous from 1
      if (idx === 0) return false; // can't deselect instalment 1
      // Can only select idx if all prior are selected
      for (let i = 0; i < idx; i++) {
        if (!selectedInstalments.includes(i + 1)) return false;
      }
      return true;
    }
  };

  const handleCheckboxChange = (idx) => {
    if (!onSelectionChange) return;
    const instalmentNum = idx + 1;
    const isSelected = selectedInstalments.includes(instalmentNum);

    let newSelection;
    if (isSelected) {
      // Deselect this and all after it
      newSelection = selectedInstalments.filter((n) => n < instalmentNum);
    } else {
      // Select this one
      newSelection = [...selectedInstalments, instalmentNum].sort((a, b) => a - b);
    }
    onSelectionChange(newSelection);
  };

  // Calculate selected total
  const selectedTotal = selectable
    ? instalments
        .filter((_, idx) => selectedInstalments.includes(idx + 1))
        .reduce((sum, inst) => sum + Number(inst.amount), 0)
    : 0;

  // For purchased mode without selectable: find next pending for pay button
  const nextPending = purchased && !selectable
    ? instalments.find((inst) => inst.status === 10 || inst.status === 30)
    : null;

  return (
    <div>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13,
        }}
      >
        <thead>
          <tr
            style={{
              backgroundColor: "#f5f5f5",
              borderBottom: "2px solid #ddd",
            }}
          >
            {selectable && <th style={thStyle}></th>}
            <th style={thStyle}>#</th>
            <th style={thStyle}>Amount</th>
            <th style={thStyle}>Due Date</th>
            <th style={thStyle}>Status</th>
            {purchased && <th style={thStyle}>Payment Date</th>}
          </tr>
        </thead>
        <tbody>
          {instalments.map((inst, idx) => {
            const instalmentNum = idx + 1;
            const isSelected = selectedInstalments.includes(instalmentNum);
            const enabled = isCheckboxEnabled(idx);
            const isPaid = purchased && inst.status === 20;

            return (
              <tr
                key={idx}
                style={{
                  borderBottom: "1px solid #eee",
                  backgroundColor: isSelected ? "#e8f5e9" : "transparent",
                }}
              >
                {selectable && (
                  <td style={tdStyle}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={!enabled && isSelected && !purchased ? idx === 0 : !enabled}
                      onChange={() => handleCheckboxChange(idx)}
                      style={{ cursor: enabled ? "pointer" : "default" }}
                    />
                  </td>
                )}
                <td style={tdStyle}>{instalmentNum}</td>
                <td style={tdStyle}>
                  {currencyCode} {inst.amount}
                </td>
                <td style={tdStyle}>
                  {inst.due_date || inst.dueDate
                    ? formatDate(inst.due_date || inst.dueDate)
                    : inst.dueDay != null
                      ? inst.dueDay === 0
                        ? "At purchase"
                        : `${inst.dueDay} day${inst.dueDay > 1 ? "s" : ""} after purchase`
                      : "-"}
                </td>
                {inst.status != null && (
                  <td style={tdStyle}>{getStatusBadge(inst.status)}</td>
                )}
                {inst.status == null && (
                  <td style={tdStyle}>-</td>
                )}
                {purchased && (
                  <td style={tdStyle}>
                    {(inst.payment_date || inst.paymentDate || inst.paid_date)
                      ? formatDate(inst.payment_date || inst.paymentDate || inst.paid_date)
                      : "-"}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {selectable && selectedInstalments.length > 0 && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 10px",
            backgroundColor: "#e8f5e9",
            borderRadius: 4,
            fontSize: 13,
            fontWeight: 600,
            color: "#2e7d32",
          }}
        >
          Selected total: {currencyCode} {selectedTotal.toFixed(2)}
          <span style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
            ({selectedInstalments.length} of {instalments.length} installments)
          </span>
        </div>
      )}

      {selectable && onPayNext && selectedInstalments.length > 0 && (
        <div style={{ marginTop: 12, textAlign: "right" }}>
          <button
            onClick={() => onPayNext(selectedInstalments, selectedTotal)}
            style={{
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Pay Selected ({currencyCode} {selectedTotal.toFixed(2)})
          </button>
        </div>
      )}

      {!selectable && purchased && nextPending && onPayNext && (
        <div style={{ marginTop: 12, textAlign: "right" }}>
          <button
            onClick={() => onPayNext(nextPending)}
            style={{
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Pay Next Installment ({currencyCode} {nextPending.amount})
          </button>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: "8px 10px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: 12,
};

const tdStyle = {
  padding: "8px 10px",
  textAlign: "left",
};

export default InstalmentSchedule;
